import React, { useState } from "react";
import { observer } from "mobx-react";
import { Button, Dropdown, Menu, Tooltip } from "antd";
import { CopyOutlined } from "@ant-design/icons";

import format from "@app/lib/format";
import notify from "@app/components/notify";
import cerConfigStore from "@app/state/store/cer-config";

import { hasSectionData } from "./fields";

const sourceLabel = (source) => {
    const when = source.updated ? format.date(source.updated) : null;
    return when ? `${source.label} — ${when}` : source.label;
};

const CopyFrom = observer(({ template, sectionKey = null, onApply }) => {
    const [applying, setApplying] = useState(false);
    const sources = cerConfigStore.sources || [];

    if (!sources.length) {
        return null;
    }

    const coversSection = (source) => !sectionKey || (source.sections || []).includes(sectionKey);

    const apply = async (source) => {
        setApplying(true);
        try {
            const config = await cerConfigStore.loadSource(source.kind, source.id);
            if (!config) {
                return;
            }
            if (sectionKey && !hasSectionData(config, template, sectionKey)) {
                notify.warn("That configuration has nothing filled in for this section");
                return;
            }
            onApply(config);
        } finally {
            setApplying(false);
        }
    };

    const menu = (
        <Menu>
            {sources.map((source) => (
                <Menu.Item
                    key={`${source.kind}:${source.id}`}
                    disabled={!coversSection(source)}
                    onClick={() => apply(source)}
                >
                    {sourceLabel(source)}
                    {!coversSection(source) && (
                        <span className="copy-from-empty"> — did not cover this section</span>
                    )}
                </Menu.Item>
            ))}
        </Menu>
    );

    const title = sectionKey
        ? "Fill this section from a configuration already made in this project."
        : "Fill this form from a configuration already made in this project. Nothing is saved until you save.";

    return (
        <Tooltip title={title}>
            <Dropdown overlay={menu} trigger={["click"]} disabled={applying}>
                <Button size="small" icon={<CopyOutlined />} loading={applying}>
                    {sectionKey ? "Load section" : "Load configuration"}
                </Button>
            </Dropdown>
        </Tooltip>
    );
});

export default CopyFrom;
